import React, { useState, useEffect } from "react";
import axios from "axios";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';
import { useAppConfig } from '../context/AppConfigContext';
import EmployeeAvailabilityModal from './EmployeeAvailabilityModal';
import AvailabilityBadge from './AvailabilityBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmployeesManager = () => {
  const { appConfig } = useAppConfig();
  const companyName = appConfig?.company_name || null;
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {employeeId, activeMissions, totalMissions}
  
  // États pour le récapitulatif mensuel
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showAddMissionModal, setShowAddMissionModal] = useState(false);
  const [showEditMissionModal, setShowEditMissionModal] = useState(false);
  const [selectedEmployeeForMission, setSelectedEmployeeForMission] = useState(null);
  const [editingMission, setEditingMission] = useState(null);
  const [housings, setHousings] = useState([]);
  const [activeTab, setActiveTab] = useState('recap'); // 'recap' ou 'liste'
  // Tri du récapitulatif : 'date_desc' (par défaut) ou 'housing_asc' (A-Z sur le logement)
  const [sortMode, setSortMode] = useState('date_desc');
  
  // Modal disponibilité employé
  const [availabilityModalEmployee, setAvailabilityModalEmployee] = useState(null);
  
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    telephone: "",
    email: "",
    disponibilites: [],
    heure_debut: "08:00",
    heure_fin: "17:00",
    type_employe: "intervenant"
  });

  const daysOfWeek = [
    { value: "lundi", label: "Lundi" },
    { value: "mardi", label: "Mardi" },
    { value: "mercredi", label: "Mercredi" },
    { value: "jeudi", label: "Jeudi" },
    { value: "vendredi", label: "Vendredi" },
    { value: "samedi", label: "Samedi" },
    { value: "dimanche", label: "Dimanche" }
  ];

  useEffect(() => {
    loadEmployees();
    loadMonthlySummary();
    loadHousings();
  }, []);

  useEffect(() => {
    loadMonthlySummary();
  }, [selectedMonth, selectedYear]);

  const loadEmployees = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des employées:", error);
      toast.info("Erreur lors du chargement des employées");
    } finally {
      setLoading(false);
    }
  };

  const loadHousings = async () => {
    try {
      const response = await axios.get(`${API}/housings`);
      setHousings(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des logements:", error);
    }
  };

  const loadMonthlySummary = async () => {
    setSummaryLoading(true);
    try {
      const response = await axios.get(`${API}/employees/monthly-summary/${selectedYear}/${selectedMonth}`);
      setMonthlySummary(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement du récapitulatif:", error);
      toast.info("Erreur lors du chargement du récapitulatif mensuel");
    } finally {
      setSummaryLoading(false);
    }
  };

  const exportEmployeesPDF = () => {
    if (!monthlySummary || !monthlySummary.employees || monthlySummary.employees.length === 0) {
      toast.error("Aucune donnée à exporter.");
      return;
    }

    try {
      const doc = new jsPDF();
      const monthNames = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
                          "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
      
      // Titre
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text(companyName ? `${companyName} - Résumé Employés` : 'Résumé Employés', 105, 20, { align: 'center' });
      
      // Période
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`${monthNames[selectedMonth - 1]} ${selectedYear}`, 105, 30, { align: 'center' });
      
      // Synthèse globale
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Synthèse Globale', 14, 45);
      
      const totalMissions = monthlySummary.employees.reduce((sum, emp) => sum + emp.total_missions, 0);
      const totalPayer = monthlySummary.employees.reduce((sum, emp) => sum + emp.total_a_payer, 0);
      const totalParures = monthlySummary.employees.reduce((sum, emp) => sum + emp.total_parures, 0);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total missions: ${totalMissions}`, 14, 55);
      doc.text(`Total à payer: ${totalPayer.toFixed(2)}€`, 14, 62);
      doc.text(`Total parures collectées: ${totalParures}`, 14, 69);
      
      // Tableau détaillé par employé
      const employeeData = monthlySummary.employees.map(emp => [
        emp.employee_name,
        emp.total_missions,
        emp.total_parures,
        emp.total_a_payer.toFixed(2) + '€'
      ]);
      
      autoTable(doc, {
        startY: 80,
        head: [['Employé', 'Missions', 'Parures', 'Total à payer']],
        body: employeeData,
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246] },
        styles: { fontSize: 10 },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 30, halign: 'center' },
          2: { cellWidth: 30, halign: 'center' },
          3: { cellWidth: 40, halign: 'right' }
        }
      });
      
      // Total en bas
      let yPos = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setFillColor(59, 130, 246);
      doc.rect(14, yPos, 182, 10, 'F');
      doc.setTextColor(255, 255, 255);
      doc.text(`TOTAL À PAYER: ${totalPayer.toFixed(2)}€`, 105, yPos + 7, { align: 'center' });
      
      // Sauvegarder
      const filename = `employes_${monthNames[selectedMonth - 1]}_${selectedYear}.pdf`;
      doc.save(filename);
      
      toast.success("PDF généré avec succès !");
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      toast.error(`Erreur lors de la génération du PDF: ${error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.id}`, formData);
      } else {
        await axios.post(`${API}/employees`, formData);
      }
      setShowModal(false);
      setEditingEmployee(null);
      resetForm();
      await loadEmployees();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement:", error);
      toast.info("Erreur lors de l'enregistrement de l'employée");
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      nom: employee.nom,
      prenom: employee.prenom,
      telephone: employee.telephone,
      email: employee.email || "",
      disponibilites: employee.disponibilites,
      heure_debut: employee.heure_debut,
      heure_fin: employee.heure_fin,
      type_employe: employee.type_employe || "intervenant"
    });
    setShowModal(true);
  };

  const handleDelete = async (employeeId) => {
    try {
      // Première tentative de suppression (sans force)
      await axios.delete(`${API}/employees/${employeeId}`);
      toast.success("Employée supprimée avec succès");
      await loadEmployees();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      
      // Si erreur 409 = missions assignées, afficher modal de confirmation
      if (error.response?.status === 409) {
        const errorData = error.response?.data?.detail;
        const activeMissions = errorData?.active_missions || 0;
        const totalMissions = errorData?.total_missions || 0;
        
        // Afficher le modal de confirmation
        setDeleteConfirm({
          employeeId,
          activeMissions,
          totalMissions
        });
      } else {
        // Autre type d'erreur
        const errorMessage = error.response?.data?.detail || "Erreur lors de la suppression de l'employée";
        toast.error(`${errorMessage}`);
      }
    }
  };
  
  const confirmForceDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      // Suppression forcée (désassigne les missions)
      await axios.delete(`${API}/employees/${deleteConfirm.employeeId}?force=true`);
      toast.success(`Employée supprimée avec succès. ${deleteConfirm.totalMissions} mission(s) désassignée(s).`);
      setDeleteConfirm(null);
      await loadEmployees();
    } catch (error) {
      console.error("Erreur lors de la suppression forcée:", error);
      const errorMessage = error.response?.data?.detail || "Erreur lors de la suppression de l'employée";
      toast.error(`${errorMessage}`);
      setDeleteConfirm(null);
    }
  };

  const resetForm = () => {
    setFormData({
      nom: "",
      prenom: "",
      telephone: "",
      email: "",
      disponibilites: [],
      heure_debut: "08:00",
      heure_fin: "17:00",
      type_employe: "intervenant"
    });
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      disponibilites: prev.disponibilites.includes(day)
        ? prev.disponibilites.filter(d => d !== day)
        : [...prev.disponibilites, day]
    }));
  };

  const formatDays = (days) => {
    return days.map(day => {
      const dayObj = daysOfWeek.find(d => d.value === day);
      return dayObj ? dayObj.label : day;
    }).join(", ");
  };

  // Fonctions pour le récapitulatif mensuel
  const handleAddMission = (employeeId) => {
    setSelectedEmployeeForMission(employeeId);
    setShowAddMissionModal(true);
  };

  const handleEditMission = (mission) => {
    // Prix actuel = prix de base du logement (pas le prix exceptionnel)
    const basePriceFromHousing = mission.prix_base_logement || mission.prix_intervenant || 0;
    
    setEditingMission({
      ...mission,
      // Prix de base du logement (non modifiable)
      current_price: basePriceFromHousing,
      // Prix exceptionnel existant (modifiable)
      editable_prix_intervenant: mission.prix_intervenant_exceptionnel || ''
    });
    setShowEditMissionModal(true);
  };

  const handleSaveMissionEdit = async () => {
    // Indicateur de sauvegarde
    const originalButtonText = "Enregistrer";
    
    try {
      const updatePayload = {
        prix_intervenant_exceptionnel: editingMission.editable_prix_intervenant ? parseFloat(editingMission.editable_prix_intervenant) : null
      };
      
      console.log('📝 Mise à jour mission:', editingMission.id, updatePayload);
      
      const response = await axios.put(`${API}/missions/${editingMission.id}`, updatePayload);
      
      console.log('✅ Réponse serveur:', response.data);
      
      // Fermer le modal immédiatement
      setShowEditMissionModal(false);
      setEditingMission(null);
      
      // Recharger les données
      await loadMonthlySummary();
      
      // Afficher le message de succès APRÈS le rechargement
      toast.success("Mission mise à jour avec succès !");
      
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour:', error);
      toast.error(`Erreur lors de la mise à jour de la mission:\n${error.response?.data?.detail || error.message}`);
    }
  };

  const handleExportSummary = (employeeData) => {
    const monthNames = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
                        "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
    
    let text = `Récapitulatif ${monthNames[selectedMonth - 1]} ${selectedYear}\n`;
    text += `Employée : ${employeeData.employee_name}\n`;
    text += `\n`;
    
    employeeData.missions.forEach(mission => {
      const date = new Date(mission.date_mission).toLocaleDateString('fr-FR');
      const prix = mission.prix_intervenant !== null ? `${mission.prix_intervenant}€` : "⚠️ Prix manquant";
      text += `${date} - ${mission.housing_pseudo} (${mission.housing_address}) - ${prix}\n`;
    });
    
    text += `\n📊 TOTAL : ${employeeData.total}€`;
    if (employeeData.missing_price_count > 0) {
      text += `\n⚠️ ${employeeData.missing_price_count} mission(s) sans prix`;
    }
    
    return text;
  };

  const copyToClipboard = (employeeData) => {
    const text = handleExportSummary(employeeData);
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Récapitulatif copié dans le presse-papiers !");
    }).catch(err => {
      console.error("Erreur lors de la copie:", err);
      toast.error("Erreur lors de la copie");
    });
  };

  const exportToWhatsApp = (employeeData) => {
    const text = handleExportSummary(employeeData);
    const encodedText = encodeURIComponent(text);
    window.open(`https://wa.me/?text=${encodedText}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Chargement des employées...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-3 px-4" data-testid="employees-manager">
      {/* Header */}
      <div className="mb-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">👥 Gestion des Employées</h1>
            <p className="text-gray-600 mt-2">
              Gérez les informations et disponibilités de vos employées
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setEditingEmployee(null);
              setShowModal(true);
            }}
            className="btn-primary"
            data-testid="add-employee-btn"
          >
            ➕ Nouvelle Employée
          </button>
        </div>
      </div>

      {/* Sous-menu */}
      <div className="mb-6">
        <div className="flex space-x-2 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('recap')}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'recap'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            📊 Récapitulatif Mensuel
          </button>
          <button
            onClick={() => setActiveTab('liste')}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'liste'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            📋 Liste des Employées ({employees.length})
          </button>
        </div>
      </div>

      {/* Section Récapitulatif Mensuel */}
      {activeTab === 'recap' && (
      <div className="mb-8">
        <div className="card">
          <div className="card-header">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">📊 Récapitulatif Mensuel</h2>
              <div className="flex items-center space-x-3">
                <button
                  onClick={exportEmployeesPDF}
                  className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!monthlySummary || !monthlySummary.employees || monthlySummary.employees.length === 0}
                >
                  📄 Exporter PDF
                </button>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="form-input px-3 py-1"
                >
                  <option value="1">Janvier</option>
                  <option value="2">Février</option>
                  <option value="3">Mars</option>
                  <option value="4">Avril</option>
                  <option value="5">Mai</option>
                  <option value="6">Juin</option>
                  <option value="7">Juillet</option>
                  <option value="8">Août</option>
                  <option value="9">Septembre</option>
                  <option value="10">Octobre</option>
                  <option value="11">Novembre</option>
                  <option value="12">Décembre</option>
                </select>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="form-input px-3 py-1"
                >
                  <option value="2024">2024</option>
                  <option value="2025">2025</option>
                  <option value="2026">2026</option>
                  <option value="2027">2027</option>
                </select>
              </div>
            </div>
          </div>
          <div className="card-body">
            {summaryLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="loading-spinner"></div>
                <span className="ml-2 text-gray-600">Chargement du récapitulatif...</span>
              </div>
            ) : monthlySummary && monthlySummary.employees && monthlySummary.employees.length > 0 ? (
              <div className="space-y-2">
                {monthlySummary.employees.map((employeeData) => (
                  <div key={employeeData.employee_id} className="border border-gray-200 rounded-lg p-2">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-base font-bold text-gray-900">{employeeData.employee_name}</h3>
                        <span className="badge badge-info text-xs">{employeeData.missions_count} missions</span>
                        <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 py-1 rounded-full text-sm font-bold shadow-md">
                          💰 Total : {employeeData.total}€
                        </span>
                        {employeeData.missing_price_count > 0 && (
                          <span className="badge badge-danger animate-pulse text-xs">⚠️ {employeeData.missing_price_count} prix manquant(s)</span>
                        )}
                      </div>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleAddMission(employeeData.employee_id)}
                          className="btn-secondary text-sm"
                        >
                          ➕ Ajouter une ligne
                        </button>
                        <button
                          onClick={() => copyToClipboard(employeeData)}
                          className="btn-secondary text-sm"
                        >
                          📋 Copier
                        </button>
                        <button
                          onClick={() => exportToWhatsApp(employeeData)}
                          className="btn-primary text-sm"
                        >
                          💬 WhatsApp
                        </button>
                      </div>
                    </div>

                    {employeeData.missions_count > 0 ? (
                      <>
                        <div className="overflow-x-auto">
                          <table className="table" style={{borderCollapse: 'collapse', lineHeight: '1.2'}}>
                            <thead>
                              <tr>
                                <th className="py-0 px-2 text-xs">Date</th>
                                <th className="py-0 px-2 text-xs">
                                  <button
                                    type="button"
                                    onClick={() => setSortMode(sortMode === 'housing_asc' ? 'date_desc' : 'housing_asc')}
                                    className={`inline-flex items-center gap-1 hover:text-blue-600 transition-colors ${sortMode === 'housing_asc' ? 'text-blue-600' : ''}`}
                                    title={sortMode === 'housing_asc' ? 'Tri A→Z actif. Cliquer pour revenir au tri par date' : 'Trier A→Z par logement'}
                                    data-testid="recap-sort-housing-toggle"
                                  >
                                    Client / Logement
                                    <span className="text-[10px] font-bold">
                                      {sortMode === 'housing_asc' ? '🔤▼' : '🔤'}
                                    </span>
                                  </button>
                                </th>
                                <th className="py-0 px-2 text-xs">Adresse</th>
                                <th className="py-0 px-2 text-xs">Horaires</th>
                                <th className="py-0 px-2 text-xs">Tarif (€)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {/* Tri configurable : dernier jour d'abord (défaut), ou A-Z par logement */}
                              {[...employeeData.missions].sort((a, b) => {
                                if (sortMode === 'housing_asc') {
                                  const nameA = (a.housing_pseudo || a.client_name || a.housing_adresse || a.notes || '').toString().toLowerCase();
                                  const nameB = (b.housing_pseudo || b.client_name || b.housing_adresse || b.notes || '').toString().toLowerCase();
                                  const cmp = nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
                                  if (cmp !== 0) return cmp;
                                  // Tie-breaker par date pour un ordre stable
                                  return new Date(a.date_mission) - new Date(b.date_mission);
                                }
                                return new Date(b.date_mission) - new Date(a.date_mission);
                              }).map((mission) => {
                                // Déterminer si la date est passée (avant aujourd'hui)
                                const missionDate = new Date(mission.date_mission);
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                missionDate.setHours(0, 0, 0, 0);
                                const isPast = missionDate < today;
                                
                                return (
                                <tr 
                                  key={mission.id}
                                  onClick={() => handleEditMission(mission)}
                                  className={`cursor-pointer hover:bg-gray-50 ${
                                    mission.has_missing_price && !mission.is_exclusive_employee ? 'bg-red-100' : 
                                    mission.is_exclusive_employee ? 'bg-purple-50' : 
                                    isPast ? 'bg-yellow-100' :  // Surligner en jaune si date passée
                                    ''
                                  }`}
                                  title={mission.is_exclusive_employee ? "Tarif interne - Cliquez pour modifier" : mission.has_missing_price ? "⚠️ Cliquez pour ajouter le tarif" : "Cliquez pour modifier"}
                                >
                                  <td className="whitespace-nowrap px-2" style={{padding: '2px 8px'}}>
                                    <span className="text-sm">{new Date(mission.date_mission).toLocaleDateString('fr-FR')}</span>
                                  </td>
                                  <td className="px-2" style={{padding: '2px 8px'}}>
                                    <div className="font-medium text-sm leading-tight">
                                      {mission.housing_pseudo ? (
                                        mission.housing_pseudo
                                      ) : (
                                        <button
                                          onClick={() => handleEditMission(mission)}
                                          className="text-blue-600 font-bold hover:text-blue-800 hover:underline"
                                          title="Modifier cette mission DIVERS"
                                        >
                                          DIVERS
                                        </button>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-500 leading-tight">
                                      {mission.client_name || <span className="italic">Mission spéciale</span>}
                                    </div>
                                  </td>
                                  <td className="text-sm px-2" style={{padding: '2px 8px'}}>{mission.housing_address}</td>
                                  <td className="text-sm whitespace-nowrap px-2" style={{padding: '2px 8px'}}>
                                    {mission.heure_debut || 'N/A'} - {mission.heure_fin || 'N/A'}
                                  </td>
                                  <td className="font-bold px-2" style={{padding: '2px 8px'}}>
                                    {mission.is_exclusive_employee ? (
                                      <span className="text-purple-600 italic">Tarif interne</span>
                                    ) : (mission.prix_intervenant_exceptionnel || mission.prix_intervenant) !== null ? (
                                      <span className="text-green-600">
                                        {mission.prix_intervenant_exceptionnel || mission.prix_intervenant}€
                                        {mission.prix_intervenant_exceptionnel && (
                                          <span className="text-orange-500 ml-1 text-xs">(exceptionnel)</span>
                                        )}
                                      </span>
                                    ) : (
                                      <span className="text-red-600">⚠️ Prix manquant</span>
                                    )}
                                  </td>
                                </tr>
                                );
                              })}
                              <tr className="bg-blue-50 font-bold">
                                <td colSpan="4" className="text-right py-1.5 px-2">TOTAL À PAYER :</td>
                                <td className="text-blue-600 text-lg py-1.5 px-2">{employeeData.total}€</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-3 text-gray-500">
                        Aucune mission ce mois-ci
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📅</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Aucune mission ce mois-ci
                </h3>
                <p className="text-gray-500">
                  Sélectionnez un autre mois ou ajoutez des missions
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Liste des employées */}
      {activeTab === 'liste' && (
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">
            Liste des Employées ({employees.length})
          </h2>
        </div>
        <div className="card-body">
          {employees.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">👥</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucune employée enregistrée
              </h3>
              <p className="text-gray-500 mb-4">
                Commencez par ajouter votre première employée
              </p>
              <button
                onClick={() => {
                  resetForm();
                  setEditingEmployee(null);
                  setShowModal(true);
                }}
                className="btn-primary"
              >
                Ajouter une employée
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Nom</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden md:table-cell">Téléphone</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden lg:table-cell">Email</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden sm:table-cell">Disponibilités</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden md:table-cell">Horaires</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider sticky right-0 bg-gray-50 shadow-[-2px_0_4px_rgba(0,0,0,0.05)]">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(() => {
                    // Séparer Propriétaires des Intervenants par type_employe
                    const proprietaires = employees.filter(emp => emp.type_employe === 'proprietaire');
                    const intervenants = employees.filter(emp => emp.type_employe !== 'proprietaire');

                    return (
                      <>
                        {/* Propriétaires en premier */}
                        {proprietaires.map(proprietaire => (
                          <tr key={proprietaire.id} className="bg-purple-50 border-b-2 border-purple-300 hover:bg-purple-100 transition-colors" data-testid={`employee-row-${proprietaire.id}`}>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <div className="font-bold text-gray-900 text-sm">
                                {proprietaire.prenom} {proprietaire.nom || ''}
                                <span className="ml-2 px-2 py-0.5 bg-purple-600 text-white text-xs rounded-full">Gérant</span>
                                <span className="ml-2 inline-block align-middle"><AvailabilityBadge employee={proprietaire} size="xs" /></span>
                              </div>
                              {/* Lien unique planning - Mobile optimized */}
                              <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-2">
                                <a 
                                  href={`/planning-employe/${proprietaire.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-xs sm:text-sm font-medium transition-colors"
                                >
                                  🔗 <span className="hidden xs:inline">Lien</span> Planning
                                </a>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(`${window.location.origin}/planning-employe/${proprietaire.id}`);
                                    toast.success("Lien copié !");
                                  }}
                                  className="inline-flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-xs sm:text-sm font-medium transition-colors"
                                  title="Copier le lien"
                                >
                                  📋 Copier
                                </button>
                              </div>
                              <div className="text-xs text-purple-600 font-semibold mt-1">
                                Prix Gérant (€)
                              </div>
                              {/* Mobile: Afficher tél et email sous le nom */}
                              <div className="md:hidden mt-1 space-y-0.5">
                                <div className="text-xs">
                                  <a href={`tel:${proprietaire.telephone}`} className="text-blue-600">
                                    📞 {proprietaire.telephone}
                                  </a>
                                </div>
                                {proprietaire.email && (
                                  <div className="text-xs">
                                    <a href={`mailto:${proprietaire.email}`} className="text-blue-600 truncate block">
                                      ✉️ {proprietaire.email}
                                    </a>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap hidden md:table-cell">
                              <a href={`tel:${proprietaire.telephone}`} className="text-blue-600 hover:text-blue-800 text-sm">
                                {proprietaire.telephone}
                              </a>
                            </td>
                            <td className="px-3 py-1.5 hidden lg:table-cell">
                              {proprietaire.email ? (
                                <a href={`mailto:${proprietaire.email}`} className="text-blue-600 hover:text-blue-800 text-sm truncate block max-w-xs">
                                  {proprietaire.email}
                                </a>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 hidden sm:table-cell">
                              <div className="text-xs">
                                {proprietaire.disponibilites.length > 0 ? (
                                  <span className="inline-block bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                    {formatDays(proprietaire.disponibilites)}
                                  </span>
                                ) : (
                                  <span className="inline-block bg-gray-100 text-gray-600 px-2 py-0.5 rounded">Aucune</span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap hidden md:table-cell">
                              <span className="text-xs text-gray-600">
                                {proprietaire.heure_debut} - {proprietaire.heure_fin}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap text-right sticky right-0 bg-purple-50">
                              <div className="flex justify-end space-x-1">
                                <button
                                  onClick={() => setAvailabilityModalEmployee(proprietaire)}
                                  className="inline-flex items-center px-2 py-1 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded text-xs font-medium transition-colors"
                                  data-testid={`availability-gear-${proprietaire.id}`}
                                  title="Disponibilité"
                                >
                                  📅
                                </button>
                                <button
                                  onClick={() => handleEdit(proprietaire)}
                                  className="inline-flex items-center px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-xs font-medium transition-colors"
                                  data-testid={`edit-employee-${proprietaire.id}`}
                                  title="Modifier"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => handleDelete(proprietaire.id)}
                                  className="inline-flex items-center px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded text-xs font-medium transition-colors"
                                  data-testid={`delete-employee-${proprietaire.id}`}
                                  title="Supprimer"
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        
                        {/* Séparateur */}
                        {proprietaires.length > 0 && intervenants.length > 0 && (
                          <tr className="bg-slate-100">
                            <td colSpan="6" className="py-2 px-3 text-xs text-slate-700 font-bold uppercase tracking-wider text-center">
                              Intervenants Externes
                            </td>
                          </tr>
                        )}
                        
                        {/* Intervenants externes */}
                        {intervenants.map((employee) => (
                          <tr key={employee.id} className="hover:bg-gray-50 transition-colors" data-testid={`employee-row-${employee.id}`}>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <div className="font-medium text-gray-900 text-sm">
                                {employee.prenom} {employee.nom || ''}
                                <span className="ml-2 px-2 py-0.5 bg-gray-500 text-white text-xs rounded-full">Intervenant</span>
                                <span className="ml-2 inline-block align-middle"><AvailabilityBadge employee={employee} size="xs" /></span>
                              </div>
                              {/* Lien unique planning - Mobile optimized */}
                              <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-2">
                                <a 
                                  href={`/planning-employe/${employee.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-xs sm:text-sm font-medium transition-colors"
                                >
                                  🔗 <span className="hidden xs:inline">Lien</span> Planning
                                </a>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(`${window.location.origin}/planning-employe/${employee.id}`);
                                    toast.success("Lien copié !");
                                  }}
                                  className="inline-flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-xs sm:text-sm font-medium transition-colors"
                                  title="Copier le lien"
                                >
                                  📋 Copier
                                </button>
                              </div>
                              <div className="text-xs text-gray-500 font-medium mt-1">
                                Prix Intervenant (€)
                              </div>
                              {/* Mobile: Afficher tél et email sous le nom */}
                              <div className="md:hidden mt-1 space-y-0.5">
                                <div className="text-xs">
                                  <a href={`tel:${employee.telephone}`} className="text-blue-600">
                                    📞 {employee.telephone}
                                  </a>
                                </div>
                                {employee.email && (
                                  <div className="text-xs">
                                    <a href={`mailto:${employee.email}`} className="text-blue-600 truncate block">
                                      ✉️ {employee.email}
                                    </a>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap hidden md:table-cell">
                              <a href={`tel:${employee.telephone}`} className="text-blue-600 hover:text-blue-800 text-sm">
                                {employee.telephone}
                              </a>
                            </td>
                            <td className="px-3 py-1.5 hidden lg:table-cell">
                              {employee.email ? (
                                <a href={`mailto:${employee.email}`} className="text-blue-600 hover:text-blue-800 text-sm truncate block max-w-xs">
                                  {employee.email}
                                </a>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 hidden sm:table-cell">
                              <div className="text-xs">
                                {employee.disponibilites.length > 0 ? (
                                  <span className="inline-block bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                    {formatDays(employee.disponibilites)}
                                  </span>
                                ) : (
                                  <span className="inline-block bg-gray-100 text-gray-600 px-2 py-0.5 rounded">Aucune</span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap hidden md:table-cell">
                              <span className="text-xs text-gray-600">
                                {employee.heure_debut} - {employee.heure_fin}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap text-right sticky right-0 bg-white">
                              <div className="flex justify-end space-x-1">
                                <button
                                  onClick={() => setAvailabilityModalEmployee(employee)}
                                  className="inline-flex items-center px-2 py-1 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded text-xs font-medium transition-colors"
                                  data-testid={`availability-gear-${employee.id}`}
                                  title="Disponibilité"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="3"/>
                                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleEdit(employee)}
                                  className="inline-flex items-center px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-xs font-medium transition-colors"
                                  data-testid={`edit-employee-${employee.id}`}
                                  title="Modifier"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => handleDelete(employee.id)}
                                  className="inline-flex items-center px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded text-xs font-medium transition-colors"
                                  data-testid={`delete-employee-${employee.id}`}
                                  title="Supprimer"
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Modal de création/édition */}
      {showModal && (
        <div className="modal-overlay" data-testid="employee-modal">
          <div className="modal-content">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingEmployee ? "Modifier l'employée" : "Nouvelle employée"}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingEmployee(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
                data-testid="close-modal-btn"
              >
                ❌
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.prenom}
                    onChange={(e) => setFormData(prev => ({...prev, prenom: e.target.value}))}
                    className="form-input"
                    data-testid="employee-prenom-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom (optionnel)
                  </label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData(prev => ({...prev, nom: e.target.value}))}
                    className="form-input"
                    data-testid="employee-nom-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.telephone}
                    onChange={(e) => setFormData(prev => ({...prev, telephone: e.target.value}))}
                    className="form-input"
                    data-testid="employee-telephone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email (optionnel)
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                    className="form-input"
                    data-testid="employee-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    value={formData.type_employe}
                    onChange={(e) => setFormData(prev => ({...prev, type_employe: e.target.value}))}
                    className="form-select"
                    data-testid="employee-type-select"
                  >
                    <option value="intervenant">🔧 Intervenant externe</option>
                    <option value="proprietaire">🏠 Gérant</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Disponibilités
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {daysOfWeek.map((day) => (
                    <label key={day.value} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.disponibilites.includes(day.value)}
                        onChange={() => handleDayToggle(day.value)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        data-testid={`employee-day-${day.value}`}
                      />
                      <span className="text-sm text-gray-700">{day.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Heure de début
                  </label>
                  <input
                    type="time"
                    value={formData.heure_debut}
                    onChange={(e) => setFormData(prev => ({...prev, heure_debut: e.target.value}))}
                    className="form-input"
                    data-testid="employee-heure-debut-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Heure de fin
                  </label>
                  <input
                    type="time"
                    value={formData.heure_fin}
                    onChange={(e) => setFormData(prev => ({...prev, heure_fin: e.target.value}))}
                    className="form-input"
                    data-testid="employee-heure-fin-input"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingEmployee(null);
                    resetForm();
                  }}
                  className="btn-secondary"
                  data-testid="cancel-employee-btn"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  data-testid="save-employee-btn"
                >
                  {editingEmployee ? "Modifier" : "Créer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center mb-4">
              <div className="text-6xl mb-2">⚠️</div>
              <h3 className="text-xl font-bold text-red-600">ATTENTION - Suppression d'Employée</h3>
            </div>
            
            <div className="mb-6 text-gray-700">
              <p className="mb-3 text-center font-semibold">
                Cette employée a <span className="text-red-600 font-bold">{deleteConfirm.activeMissions} mission(s) assignée(s)</span>
                {deleteConfirm.totalMissions !== deleteConfirm.activeMissions && 
                  ` (${deleteConfirm.totalMissions} au total)`
                }
              </p>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-3">
                <p className="text-sm">
                  Si vous continuez, l'employée sera supprimée et <strong>TOUTES SES MISSIONS seront désassignées</strong> (les missions ne seront pas supprimées, mais n'auront plus d'employée assignée).
                </p>
              </div>
              
              <p className="text-center text-red-600 font-bold">
                ⚠️ Cette action est IRRÉVERSIBLE ! ⚠️
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                ❌ Annuler
              </button>
              <button
                onClick={confirmForceDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-bold"
              >
                🗑️ Supprimer et Désassigner
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'ajout de mission */}
      {showAddMissionModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">➕ Ajouter une mission</h3>
              <button
                onClick={() => setShowAddMissionModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ❌
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Cette mission sera ajoutée au planning et au récapitulatif de l'employée.
            </p>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const housing_id = formData.get('housing_id');
              const date_mission = formData.get('date_mission');
              const isManual = formData.get('saisie_manuelle') === 'on';
              
              if (!isManual && !housing_id) {
                alert("Veuillez sélectionner un logement");
                return;
              }
              if (!date_mission) {
                alert("Veuillez sélectionner une date");
                return;
              }
              
              try {
                const payload = {
                  date_mission,
                  employee_id: selectedEmployeeForMission,
                  heure_debut: formData.get('heure_debut') || null,
                  heure_fin: formData.get('heure_fin') || null,
                  notes: formData.get('notes') || null
                };
                
                if (isManual) {
                  payload.notes = formData.get('lieu_manuel') || formData.get('notes') || 'Mission manuelle';
                  payload.prix_intervenant = formData.get('montant_manuel') ? parseFloat(formData.get('montant_manuel')) : null;
                } else {
                  payload.housing_id = housing_id;
                }
                
                await axios.post(`${API}/missions`, payload);
                setShowAddMissionModal(false);
                // Recharger la liste (fonction pas encore implémentée : trigger via location reload light)
                if (typeof window !== 'undefined' && window.location) {
                  // Effet de bord minimal : reset flag pour forcer re-render parent
                  setSelectedEmployeeForMission(selectedEmployeeForMission);
                }
              } catch (error) {
                alert(error.response?.data?.detail || "Erreur lors de la création");
              }
            }} className="space-y-4">
              {/* Saisie manuelle toggle */}
              <label className="flex items-center gap-2 cursor-pointer bg-amber-50 border border-amber-200 rounded-lg p-2">
                <input type="checkbox" name="saisie_manuelle" onChange={(e) => {
                  const manual = e.target.closest('form').querySelector('[data-manual-section]');
                  const housing = e.target.closest('form').querySelector('[data-housing-section]');
                  if (e.target.checked) {
                    manual.classList.remove('hidden');
                    housing.classList.add('hidden');
                  } else {
                    manual.classList.add('hidden');
                    housing.classList.remove('hidden');
                  }
                }} className="rounded border-amber-300 text-amber-600" />
                <span className="text-sm font-medium text-amber-900">Saisie manuelle (pas un logement)</span>
              </label>

              {/* Logement */}
              <div data-housing-section>
                <label className="block text-sm font-medium text-gray-700 mb-1">Logement *</label>
                <select name="housing_id" className="form-select">
                  <option value="">-- Choisir un logement --</option>
                  {[...housings].sort((a, b) => (a.pseudo || a.adresse || '').localeCompare(b.pseudo || b.adresse || '')).map(h => (
                    <option key={h.id} value={h.id}>{h.pseudo || h.adresse} ({h.ville})</option>
                  ))}
                </select>
              </div>

              {/* Section saisie manuelle */}
              <div data-manual-section className="hidden space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lieu / Description *</label>
                  <input type="text" name="lieu_manuel" className="form-input" placeholder="Ex: Nettoyage vitres bureau..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Montant (€)</label>
                  <input type="number" name="montant_manuel" step="0.01" min="0" className="form-input" placeholder="Prix intervenant" />
                </div>
              </div>
              
              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input type="date" name="date_mission" className="form-input" required defaultValue={new Date().toISOString().split('T')[0]} />
              </div>
              
              {/* Horaires */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heure début</label>
                  <input type="time" name="heure_debut" className="form-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heure fin</label>
                  <input type="time" name="heure_fin" className="form-input" />
                </div>
              </div>
              
              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <input type="text" name="notes" className="form-input" placeholder="Notes optionnelles" />
              </div>
              
              <div className="flex gap-2 pt-2">
                <button type="submit" className="btn-primary flex-1">
                  ✅ Créer la mission
                </button>
                <button type="button" onClick={() => setShowAddMissionModal(false)} className="btn-secondary flex-1">
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de modification de mission */}
      {showEditMissionModal && editingMission && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">✏️ Modifier la mission</h3>
              <button
                onClick={() => {
                  setShowEditMissionModal(false);
                  setEditingMission(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ❌
              </button>
            </div>

            {editingMission.has_missing_price && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <span className="text-2xl mr-3">⚠️</span>
                  <div>
                    <p className="font-semibold text-yellow-800">Tarif manquant</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Ajoutez le tarif ci-dessous pour cette mission spécifique.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={editingMission.date_mission}
                    disabled
                    className="form-input bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Logement</label>
                  <input
                    type="text"
                    value={editingMission.housing_pseudo}
                    disabled
                    className="form-input bg-gray-100"
                  />
                </div>
              </div>

              {/* Prix actuel */}
              <div className="bg-gray-50 rounded-lg p-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  💰 Prix actuel (paramétré dans le logement)
                </label>
                <div className="text-xl font-bold text-gray-800">
                  {editingMission.current_price > 0 ? `${editingMission.current_price}€` : 'Non défini'}
                </div>
              </div>

              {/* Nouveau prix exceptionnel */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ✨ Nouveau prix (exceptionnel pour cette mission uniquement)
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    step="0.01"
                    value={editingMission.editable_prix_intervenant}
                    onChange={(e) => setEditingMission({...editingMission, editable_prix_intervenant: e.target.value})}
                    className="form-input"
                    placeholder="Laisser vide pour garder le prix actuel"
                  />
                  <span className="text-gray-500">€</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Ce prix ne modifie pas le tarif de base du logement, il s&apos;applique uniquement à cette mission.
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowEditMissionModal(false);
                    setEditingMission(null);
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveMissionEdit}
                  className="btn-primary"
                >
                  💾 Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {availabilityModalEmployee && (
        <EmployeeAvailabilityModal
          employee={availabilityModalEmployee}
          onClose={() => setAvailabilityModalEmployee(null)}
          onSaved={(updated) => {
            setEmployees(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));
          }}
        />
      )}
    </div>
  );
};

export default EmployeesManager;